import { useId, useState } from 'react'
import { Button, Loader } from '@mantine/core'
import dayjs from '@/lib/dayjs'

import ScrollableScreen from '@/components/ui/ScrollableScreen'
import DocumentTitle from '@/components/ui/DocumentTitle'
import useApiSWR from '@/hooks/useApiSWR'

const LOCAL_BACKUP = {
    disk: 'local-proxmox',
    label: 'Serveur local',
    status: 'not_configured',
}

const STATUS = {
    not_configured: 'Non configuré',
    ok: 'À jour',
    gaps: 'Incomplet',
    late: 'En retard',
    empty: 'Aucune sauvegarde',
    unreachable: 'Stockage inaccessible',
}
const formatRecentDate = (timestamp) => {
    const date = dayjs.unix(timestamp)
    const daysAgo = dayjs().startOf('day').diff(date.startOf('day'), 'day')
    if (daysAgo === 0) return 'Aujourd’hui'
    if (daysAgo === 1) return 'Hier'
    if (daysAgo > 1 && daysAgo < 7) return `Il y a ${daysAgo} jours`
    return date.format('D MMM YYYY')
}
const formatExactDate = (timestamp) => dayjs.unix(timestamp).format('D MMM YYYY à HH:mm')

const formatSchedule = (disk) => {
    if (disk.every === 'hour') return 'Toutes les heures'
    if (disk.every !== 'day') return 'Non configurée'

    const scheduled = new Date((disk.checked_at ?? Date.now() / 1000) * 1000)
    scheduled.setUTCHours(2, 30, 0, 0)
    return `Chaque jour à ${dayjs(scheduled).format('HH:mm')}`
}
const formatSize = (bytes) => bytes == null ? '—' : bytes >= 1024 ** 3
    ? `${(bytes / 1024 ** 3).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} Go`
    : `${(bytes / 1024 ** 2).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`

function DiskSection({ disk }) {
    const headingId = useId()
    const unavailable = disk.status === 'unreachable'
    const notConfigured = disk.status === 'not_configured'
    const backups = disk.backups ?? []
    const missing = (disk.slots ?? []).filter((slot) => slot.state === 'missing')
    const count = notConfigured || unavailable ? '—' : disk.count ?? backups.length
    const latest = disk.latest_at == null ? null : dayjs.unix(disk.latest_at)

    return (
        <section aria-labelledby={headingId} className="row-span-3 grid min-w-0 grid-rows-subgrid gap-y-0 overflow-hidden rounded-xl border border-gray-200 bg-white">
            <header className="flex items-start justify-between gap-4 px-6 pt-6">
                <div>
                    <h2 id={headingId} className="text-xl font-semibold tracking-tight text-gray-900">{disk.label}</h2>
                    {!notConfigured && <p className="mt-1 text-sm text-gray-500">{formatSchedule(disk)}</p>}
                </div>
                <span className={`flex items-center gap-1.5 pt-1 text-xs font-medium ${notConfigured ? 'text-gray-500' : disk.status === 'ok' ? 'text-green-700' : 'text-red-700'}`}>
                    <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${notConfigured ? 'bg-gray-400' : disk.status === 'ok' ? 'bg-green-600' : 'bg-red-600'}`} />
                    {STATUS[disk.status] ?? 'Inconnu'}
                </span>
            </header>

            <div className="flex min-h-[184px] flex-col px-6 pb-6 pt-7">
                {unavailable ? <p className="text-sm text-gray-500">Sauvegardes non vérifiables</p> : <>
                    <h3 className="text-xs font-medium text-gray-500">Dernière sauvegarde</h3>
                    <p title={latest ? formatExactDate(disk.latest_at) : undefined} className="mt-2 text-xl font-medium tabular-nums text-gray-900">
                        {latest ? `${formatRecentDate(disk.latest_at)} à ${latest.format('HH:mm')}` : '—'}
                    </p>
                    {missing.length > 0 && (
                        <div className="mt-3 text-xs leading-5 text-red-700">
                            <span className="font-medium">Manquante{missing.length > 1 ? 's' : ''} : </span>
                            {missing.map((slot) => {
                                // Use the scheduled instant, not UTC midnight, for daily labels.
                                const timestamp = disk.every === 'day'
                                    ? slot.due_at - disk.grace_minutes * 60
                                    : slot.start
                                return `${formatRecentDate(timestamp)} à ${dayjs.unix(timestamp).format('HH:mm')}`
                            }).join(' ; ')}
                        </div>
                    )}
                </>}
            </div>
            <div className="border-t border-gray-200 bg-gray-50/60">
                <div className="flex items-baseline justify-between gap-4 px-6 py-4">
                    <h3 className="text-sm font-semibold text-gray-900">Archives <span className="ml-1 font-normal tabular-nums text-gray-500">{count}</span></h3>
                    <span className="text-xs tabular-nums text-gray-500">{formatSize(disk.total_size)}</span>
                </div>
                <div className="h-64 overflow-y-auto overscroll-contain" role="region" aria-label={`Archives ${disk.label}`} tabIndex={backups.length > 0 ? 0 : undefined}>
                    <ul className="px-6">
                        {backups.map((backup) => (
                            <li key={backup.name} title={backup.name} className="border-t border-gray-200/70 py-3">
                                <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 text-sm tabular-nums">
                                    <span title={formatExactDate(backup.created_at)} className="text-gray-700">{formatRecentDate(backup.created_at)}</span>
                                    <span className="text-gray-700">{dayjs.unix(backup.created_at).format('HH:mm')}</span>
                                    <span className="w-16 text-right text-xs text-gray-500">{formatSize(backup.size)}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="h-3" />
            </div>
        </section>
    )
}

export default function BackupsPage() {
    const { data: disks, error, isLoading, mutate } = useApiSWR('/admin/backups', null, { refreshInterval: 60000 })
    const [refreshing, setRefreshing] = useState(false)
    const refresh = async () => {
        setRefreshing(true)
        try { await mutate() } catch { /* SWR exposes the refresh error below. */ }
        finally { setRefreshing(false) }
    }

    return (
        <ScrollableScreen
            breadcrumbs={[{ label: 'Administration' }, { label: 'Sauvegardes' }]}
            rightSection={<Button variant="default" size="compact-sm" onClick={refresh} loading={refreshing}>Actualiser</Button>}
        >
            <DocumentTitle title="BO - Sauvegardes - Baltic Watches" />
            <main className="@container mx-auto w-full max-w-7xl px-6 py-8">
                {isLoading ? <div className="flex justify-center py-10"><Loader aria-label="Chargement des sauvegardes" /></div> : error || !disks ? (
                    <p role="alert" className="text-sm text-red-700">Impossible de vérifier les sauvegardes.</p>
                ) : (
                    <div className="grid gap-6 @3xl:grid-cols-2 @5xl:grid-cols-3">
                        {disks.map((disk) => <DiskSection key={disk.disk} disk={disk} />)}
                        <DiskSection disk={LOCAL_BACKUP} />
                    </div>
                )}
            </main>
        </ScrollableScreen>
    )
}
